import { Alert } from '@mui/material';
import type {
  DataGridProps,
  GridRowParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import { DataGrid, GRID_CHECKBOX_SELECTION_FIELD } from '@mui/x-data-grid';
import { useCampaignList } from '@features/campaign-list';
import type { FilterParams } from '@features/campaign-list';
import { useState } from 'react';
import type { ExportCampaignRow } from './columns';
import { getExportColumns } from './columns';
import { hasDateOverlap } from '../../domain/date-window';
import type { DateWindow } from '../../domain/date-window';
import type { ExportFilterState } from './filter-state';
import { MAX_SELECTED_ROWS } from './selection-limits';

const DEFAULT_PAGE_SIZE = 5;
const REPORT_DATE_FROM = '2026-02-10';
const PAGE_SIZES = [DEFAULT_PAGE_SIZE, 10, 25] as const;
const ROW_DISABLED_CLASS = 'row-disabled';

const toFilterParams = (filterState: ExportFilterState): FilterParams => ({
  searchText: filterState.searchText,
  selectedCampaignType: filterState.selectedCampaignType,
  selectedReportType: 'overall',
  selectedClient: filterState.selectedClient,
  checkedAdvertisers: filterState.selectedAdvertiser
    ? [filterState.selectedAdvertiser]
    : [],
  reportDateFrom: REPORT_DATE_FROM,
});

const isRowOverlapping = (
  row: ExportCampaignRow,
  dateWindow: DateWindow | null
): boolean =>
  !dateWindow ||
  hasDateOverlap(row.campaignStartDate, row.campaignEndDate, dateWindow);

type ExportCampaignDataGridProps = {
  dateWindow: DateWindow | null;
  filterState: ExportFilterState;
  onSelectionChange: (selectedRows: ExportCampaignRow[]) => void;
  selectedRows: ExportCampaignRow[];
};

export const ExportCampaignDataGrid = ({
  dateWindow,
  filterState,
  onSelectionChange: handleSelectionChange,
  selectedRows,
}: ExportCampaignDataGridProps) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const { isFetching, error, campaignList, total } = useCampaignList(
    toFilterParams(filterState),
    {
      page,
      pageSize,
      sortKey: 'campaignStartDate',
      sortDirection: 'desc',
    }
  );

  if (error) return <Alert severity="error">Error loading campaign list</Alert>;

  const handlePaginationModelChange: DataGridProps['onPaginationModelChange'] =
    model => {
      setPage(model.page);
      setPageSize(model.pageSize);
    };

  const selectionModel: GridRowSelectionModel = selectedRows.map(
    r => r.campaignId
  );

  const handleSelectionModelChange: DataGridProps['onRowSelectionModelChange'] =
    newModel => {
      const newIds = new Set(newModel);
      const retained = selectedRows.filter(r => newIds.has(r.campaignId));
      const retainedIds = new Set(retained.map(r => r.campaignId));
      const added = campaignList.filter(
        r =>
          newIds.has(r.campaignId) &&
          !retainedIds.has(r.campaignId) &&
          isRowEnabled(r)
      );
      handleSelectionChange([...retained, ...added]);
    };

  const isAtMaxSelection = selectionModel.length >= MAX_SELECTED_ROWS;

  const isRowEnabled = (row: ExportCampaignRow): boolean =>
    selectionModel.includes(row.campaignId) ||
    (!isAtMaxSelection && isRowOverlapping(row, dateWindow));

  return (
    <DataGrid
      checkboxSelection
      columns={[
        { field: GRID_CHECKBOX_SELECTION_FIELD, renderHeader: () => null },
        ...getExportColumns(filterState.selectedCampaignType),
      ]}
      getRowClassName={(params: GridRowParams<ExportCampaignRow>) =>
        isRowEnabled(params.row) ? '' : ROW_DISABLED_CLASS
      }
      getRowId={row => row.campaignId}
      keepNonExistentRowsSelected
      loading={isFetching}
      onPaginationModelChange={handlePaginationModelChange}
      onRowSelectionModelChange={handleSelectionModelChange}
      pageSizeOptions={PAGE_SIZES}
      paginationMode="server"
      paginationModel={{ page, pageSize }}
      rowCount={total ?? 0}
      rowSelectionModel={selectionModel}
      rows={campaignList}
      sx={{
        height: 'auto',
        marginTop: 2,
        '--DataGrid-overlayHeight': '150px',
        [`& .${ROW_DISABLED_CLASS}`]: {
          opacity: 0.5,
          cursor: 'default',
        },
        [`& .${ROW_DISABLED_CLASS} .MuiCheckbox-root`]: {
          pointerEvents: 'none',
        },
      }}
    />
  );
};
